import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { AlertTriangle } from "lucide-react";

type TeamBlockErrorProps = {
  onRetry: () => void;
  /**
   * When true, the error replaces a chart and reserves its 250px box so the
   * card keeps the same height across the loading / empty / error states.
   */
  boxed?: boolean;
};

/**
 * Error state of a single block of the team activity page, with a retry action.
 *
 * Each block owns its own request, so this is rendered in place of that block's
 * content only — the other blocks stay on screen. The `AlertTriangle` icon
 * distinguishes it from the empty state, which uses `Activity`.
 */
export const TeamBlockError = ({
  onRetry,
  boxed = false,
}: TeamBlockErrorProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 text-center ${
        boxed ? "h-[250px]" : "py-6"
      }`}
    >
      <AlertTriangle className="h-6 w-6 text-muted-foreground" />
      <p className="text-sm text-muted-foreground text-balance">
        {t("team.activity.errors.fetchData")}
      </p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t("team.activity.errors.retry")}
      </Button>
    </div>
  );
};
